// @flow

import React, { useState, useCallback, useLayoutEffect, type Node, useEffect } from 'react';
import styled from 'styled-components';
import { useIncomingPayment, isRetry } from '@tectonics/prs/incoming-payment';
import { useFormHasBeenSent } from '@tectonics/prs/form-status';

import { useSubmitError, hasUserInputValidationError, hasSubmitError, useValidation } from '@tectonics/prs/validation';

import { scrollToTop, scrollToFirstFieldWithError } from '@tectonics/screen';

import { IfFeature } from '@tectonics/prs/feature';
import { useAllFeatures } from '@tectonics/prs/payment-spec';
import { useSubmitting } from '@tectonics/prs/submitting';
import { useFormStatusContext } from '@tectonics/prs/form-status/use-form-status-context';
import { isElementVisible } from '@tectonics/look-and-feel/commons/dom-utils';

const CustomerForm = React.lazy(() => import('../customer-form'));
const PaymentMethod = React.lazy(() => import('../payment-method'));
import { CaptchaForm } from '../captcha';
import { ErrorDisplay } from '../error-display';
import { MessageFinishYourPayment } from '../message-finish-your-payment';
import { PaymentMethodInfo } from '../payment-method-info';

import { PlaceOrderButton } from './place-order-button';
import { BackButton } from './back-button';
import { Retry } from './retry';
import { Terms } from './terms';
import { BrazilianTermsForCrossBorderAndDiamond } from './brazilian-terms-for-cross-border-and-diamond';
import { ArgentineTermsForWithholdTaxes } from './argentine-terms-for-withhold-taxes';
import { KenyanTermsForInternationalTransfer } from './kenyan-terms-for-international-transfer';
import { TextInstructionWithholdTaxesByCustomer } from '@tectonics/prs/argentina/withhold-taxes';
import { useUserInputValue } from '@tectonics/prs/user-input-values';

type Props = {|
  onSubmit: (boolean, boolean) => Promise<bool>,
|};

const FormElement = styled.form`
  display: grid;
  grid-gap: 16px;
  padding: 0 16px 32px;
  @media screen and (min-width: 840px) {
    padding: 32px;
  }
`;

export function Form({ onSubmit }: Props): Node {
  const formHasBeenSent = useFormHasBeenSent();
  const [hasScrolled, setHasScrolled] = useState<boolean>(false);
  const [, setGooglePayOutgoingPayment] = useUserInputValue('googlePayOutgoingPayment');
  const incomingPayment = useIncomingPayment();
  const submitting = useSubmitting();
  const retry = isRetry(incomingPayment);
  const allFeatures = useAllFeatures();

  const [, setFormStatus] = useFormStatusContext();
  const handleSubmit = useCallback((event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    (async () => {
      const hasErrors = Array.from(document.querySelectorAll('[data-error]'))
        .filter(isElementVisible).length > 0;

      setFormStatus('sent');
      setHasScrolled(false);
      await onSubmit(formHasBeenSent, hasErrors);
    })();
  }, [onSubmit, setFormStatus, formHasBeenSent]);

  const submitError = useSubmitError();
  const validation = useValidation();

  useLayoutEffect(() => {
    if (!hasScrolled && hasUserInputValidationError(validation)) {
      scrollToFirstFieldWithError();
      setHasScrolled(true);
    }
  }, [hasScrolled, setHasScrolled, validation]);

  useLayoutEffect(() => {
    const shouldScrollToTop = !hasScrolled && !submitting && (
      hasSubmitError(validation) ||
      retry
    );

    if (shouldScrollToTop) {
      scrollToTop();
      setHasScrolled(true);
    }
  }, [hasScrolled, setHasScrolled, validation, retry, submitting]);

  useEffect(() => {
    if (!submitting && (hasUserInputValidationError(validation) || hasSubmitError(validation))) {
      setGooglePayOutgoingPayment(undefined);
    }
  }, [validation]);

  return (
    <FormElement onSubmit={handleSubmit}>
      {retry && (
        <Retry />
      )}

      {!retry && (
        <MessageFinishYourPayment />
      )}

      {submitError && (
        <ErrorDisplay error={submitError} />
      )}

      <CustomerForm />

      <PaymentMethod rootStyle={{ marginTop: '16px' }} />
      <PaymentMethodInfo />

      <CaptchaForm />

      <BrazilianTermsForCrossBorderAndDiamond />
      <ArgentineTermsForWithholdTaxes />
      <KenyanTermsForInternationalTransfer />

      <PlaceOrderButton />
      <IfFeature allFeatures={allFeatures} wanted="display_back_button">
        <BackButton />
      </IfFeature>

      <TextInstructionWithholdTaxesByCustomer />

      <Terms />
    </FormElement>
  );
}
